import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../lib/store';
import { login } from '../lib/api';
import { Loader2, CheckCircle } from 'lucide-react';
import { getDeviceFingerprint } from '../lib/device';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login: storeLogin } = useAuthStore();
  
  const successMessage = (location.state as any)?.message;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const deviceId = await getDeviceFingerprint();
      const response = await login(email, password, deviceId);
      
      // Store user, token, and subscription status
      storeLogin(response.user, response.token, response.subscription);
      
      // Redirect admin to admin dashboard
      if (response.user.role === 'admin') {
        navigate('/admin');
      } else {
        // All users go to dashboard (features locked if not paid)
        navigate('/');
      }
    } catch (err: any) {
      if (err.message?.includes('device')) {
        setError('Account inatumika kwenye device nyingine.');
      } else if (err.message?.includes('Subscription')) {
        setError(err.message);
      } else {
        setError('Email au password si sahihi');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            CLIPD
          </h1>
          <p className="text-slate-500 mt-2">Maudhui Pendwa kwa Wananchi</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
              <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span>{successMessage}</span>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400"
              placeholder="mteja@example.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Inaingia...
              </>
            ) : (
              'Ingia'
            )}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-slate-500">
          Huna account?{' '}
          <Link to="/signup" className="text-blue-600 hover:underline">
            Jisajili
          </Link>
        </div>
      </div>
    </div>
  );
}
